// @ts-check
// The mates' weekly board, the app side: wording and numbers for the card.
// The rules (targets, typical efforts, ranking) live on the API in
// lib/board.js.

/** "7:41.8" from seconds. */
export const clock = (/** @type {number} */ s) => {
  const tenths = Math.round(s * 10);
  const m = Math.floor(tenths / 600);
  const rest = (tenths % 600) / 10;
  return `${m}:${rest < 10 ? "0" : ""}${rest.toFixed(1)}`;
};

/** A tab's number in its unit: a time for rows, watts for rides. */
export const effortValue = (/** @type {number} */ v, /** @type {string} */ unit) =>
  unit === "s" ? clock(v) : `${Math.round(v)} W`;

/** "−3.3%", "+7.3%": the change in the unit's own direction (time down, watts up). */
export const signedPct = (/** @type {number} */ improvement, /** @type {boolean} */ lower) => {
  const pct = Math.abs(improvement * 100).toFixed(1);
  const better = improvement >= 0;
  // Time: better is a fall. Watts: better is a rise.
  const sign = better === lower ? "−" : "+";
  return Number(pct) === 0 ? "0.0%" : `${sign}${pct}%`;
};

/** "−18.6 s", "+15 W". */
export const signedDelta = (/** @type {number} */ delta, /** @type {string} */ unit) => {
  const sign = delta < 0 ? "−" : delta > 0 ? "+" : "";
  return unit === "s" ? `${sign}${Math.abs(delta).toFixed(1)} s` : `${sign}${Math.abs(Math.round(delta))} W`;
};

/**
 * The line under a name on an effort tab.
 * @param {{ best: number|null, typical: number|null, count: number }} r
 * @param {{ title: string, unit: string }} tab
 */
export const effortLine = (r, tab) => {
  if (r.best == null) return `No ${tab.title.replace(/ (row|ride)$/, "")} this week`;
  if (r.typical == null) return `${effortValue(r.best, tab.unit)} · ${r.count} of 3 to set a usual`;
  return `${effortValue(r.best, tab.unit)} · usual ${effortValue(r.typical, tab.unit)}`;
};

/** How full the Sessions bar is (of the 150% cap), and where 100% sits. */
export const sessionsBar = (/** @type {number} */ pct) => ({ fill: Math.min(100, (pct / 150) * 100), full: (100 / 150) * 100 });

/** Effort tabs worth showing: anyone did one this week. */
export const liveTabs = (
  /** @type {{ key: string }[]|undefined} */ tabs,
  /** @type {Record<string, { best: number|null }[]>|undefined} */ efforts,
) => (tabs || []).filter((t) => (efforts?.[t.key] || []).some((r) => r.best != null));

/** "Mon 16 Nov". */
export const weekLabel = (/** @type {string} */ day) => {
  const d = new Date(`${day}T12:00:00Z`);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `Mon ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
};

/** A tab's label short enough for four across a phone: "2 km", "20 min". */
export const shortTab = (/** @type {{ key: string, title: string }} */ t) =>
  t.key === "sessions" ? "Sessions" : t.title.replace(/ (row|ride)$/, "");
